<template>
  <div class="services__header">
    <div class="container">
      <div class="services__header-inner">
        <div class="services__header-top">
          <div class="services__header-top-inner">
            <div class="services__header-title-wrap">
              <button @click="$router.go(-1)" class="services__header-prev-btn">
                <icon name="arrow-left" />
              </button>
              <h1 class="services__header-title">
                {{ title }}
              </h1>
            </div>
            <BreadCrumbs :links="breadcrumbs" />
          </div>
          <EventStand />
        </div>
        <div class="services__header-bottom">
          <PrimaryButton
            @click="toggleCategories"
            class="services__header-catalog-btn"
          >
            <icon
              :name="overlays.servicesCategories ? 'close' : 'catalog-menu'"
            />
            {{ $t("services.catalogBtn") }}
          </PrimaryButton>
          <div class="services__header-search">
            <InputLabel
              :dropdownVisible="(search || null) && dropdownVisible"
              v-click-outside="hideSearchDropdown"
            >
              <icon slot="icon" name="search" />
              <ServicesSearchResults :list="services" slot="dropdown" />
              <input
                @focus="handleSearchFocus"
                @keydown="handleKeyDown"
                :placeholder="$t('services.searchPlaceholder')"
                class="input"
                v-model="search"
              />
            </InputLabel>
            <PrimaryButton
              @click="openSearchPage"
              class="services__header-search-btn"
              :disabled="!search"
            >
              {{ $t("services.searchBtn") }}
            </PrimaryButton>
          </div>
          <div class="services__header-dropdown">
            <span class="services__header-dropdown-title">
              {{ $t("services.currencyTitle") }}:
            </span>
            <Dropdown
              :value="currentCurrencies"
              single
              modified
              title="₽"
              label-name="symbol"
              key-name="code"
              class="services__header-dropdown-select services__header-currency"
              @select="setCurrency"
            />
            <span class="services__header-dropdown-title">
              {{ $t("services.standTitle") }}:
            </span>
            <Dropdown
              :value="currentStands"
              single
              modified
              label-name="title"
              key-name="title"
              class="services__header-dropdown-select services__header-stand"
              @select="setStand"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="services__header-categories"
      :class="{
        'services__header-categories--visible': overlays.servicesCategories
      }"
    >
      <div class="services__header-categories-content container">
        <ServicesCategories :categories="categories" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import currencies from "@/helpers/constants/currencies";
import apiServices from "@/api/services";
import ServicesCategories from "@/components/Custom/ServicesCategories.vue";
import EventStand from "@/components/Custom/EventStand.vue";
import {
  PrimaryButton,
  Dropdown,
  InputLabel,
  BreadCrumbs
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import ServicesSearchResults from "./ServicesSearchResults.vue";

export default {
  name: "ServicesHeader",
  components: {
    EventStand,
    Dropdown,
    ServicesCategories,
    ServicesSearchResults,
    InputLabel,
    PrimaryButton,
    BreadCrumbs
  },
  props: {
    title: String,
    breadcrumbs: Array
  },
  data() {
    return {
      search: null,
      dropdownVisible: false,
      currencies: [...currencies],
      searchDebounce: null,
      services: []
    };
  },
  async created() {
    this.search = this.$route.query.s || null;
    await this.getCategories();
  },
  computed: {
    ...mapGetters("services", ["categories"]),
    ...mapGetters("event", ["eventCurrencies", "currency", "stands"]),
    ...mapState("event", ["event", "currentStand"]),

    overlays() {
      return this.$store.state.event._overlays;
    },
    currentCurrencies() {
      return this.eventCurrencies.map(currency => {
        return { ...currency, selected: currency.code === this.currency?.code };
      });
    },
    currentStands() {
      return this.stands.map(stand => {
        return { ...stand, selected: stand.id === this.currentStand?.id };
      });
    }
  },
  methods: {
    setCurrency(currency) {
      if (currency) {
        this.$store.dispatch("event/setCurrency", currency.code);
      }
    },
    setStand(stand) {
      if (stand) {
        this.$store.dispatch("event/setStand", stand);
      }
    },
    async getCategories() {
      try {
        await this.$store.dispatch(
          "services/getCategories",
          this.$route.params.id
        );
        this.$emit("loaded");
      } catch (e) {
        if (e.error_data?.response?.status === 403) {
          this.$emit("error");
        }
      }
    },
    async getServices() {
      let data = {};
      if (i18n.locale === "ru") {
        data.name_rus = this.search;
      } else if (i18n.locale === "en") {
        data.name_eng = this.search;
      }
      await apiServices
        .additionalServices(this.$route.params.id, data)
        .then(({ data }) => {
          this.services = data;
        });
    },
    toggleCategories() {
      if (window.innerWidth < 1000) {
        this.$modal.show("event-modal-categories");
      } else {
        this.$store.state.event._overlays.servicesCategories = !this.$store
          .state.event._overlays.servicesCategories;
      }
    },
    openSearchPage() {
      this.$router.push({
        name: "services-search",
        query: { s: this.search || undefined }
      });
    },
    handleSearchFocus() {
      this.$store.state.event._overlays.servicesCategories = false;
      this.dropdownVisible = true;
    },
    handleKeyDown(e) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.getServices();
      }, 500);

      if (e.keyCode === 13) {
        this.openSearchPage();
      }
    },
    hideSearchDropdown() {
      this.dropdownVisible = false;
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.services__header {
  background: var(--white, #fff);
  padding: 30px 0;
  position: relative;
  z-index: 6;
  border-bottom: 1px solid var(--gray-darker, #d5dae5);
  @media (max-width: 440px) {
    padding: 15px 0;
  }

  &-top {
    margin-bottom: 40px;
    display: flex;
    align-items: flex-start;
    @media (max-width: 680px) {
      flex-direction: column;
      margin-bottom: 15px;
    }

    &-inner {
      flex-grow: 1;
      margin-right: 20px;

      @media (max-width: 680px) {
        margin-right: 0;
        margin-bottom: 20px;
      }
    }
  }

  &-title-wrap {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    @media (max-width: 440px) {
      margin-bottom: 15px;
    }
  }

  &-title {
    margin: 0;
    @media (max-width: 400px) {
      font-size: 1rem;
      font-weight: 700;
      line-height: 24px;
    }
  }

  &-prev-btn {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: 1px solid var(--gray-darker, #d5dae5);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color var(--transition, 0.2s);
    margin-right: 15px;
    color: var(--gray-dark, #a3a8b3);
    background: none;
    cursor: pointer;

    &:hover {
      background: var(--main-bg, #f0f5f9);
    }

    .svg-icon {
      max-width: 100%;
      max-height: 100%;
      width: 20px;
      height: 20px;
    }
  }

  &-bottom {
    display: flex;

    @media (max-width: 740px) {
      flex-wrap: wrap;
    }
  }

  &-catalog-btn {
    @media (max-width: 485px) {
      flex-grow: 1;
    }
  }

  &-search {
    flex-grow: 1;
    display: flex;
    margin-left: 30px;

    @media (max-width: 740px) {
      order: 1;
      margin-left: 0;
      margin-top: 15px;
      width: 100%;
    }

    .input-label {
      flex-grow: 1;
      margin-right: 15px;
      height: 40px;

      &__dropdown {
        @media (max-width: 1000px) {
          right: -80px;
        }
      }

      input {
        height: 40px;
        transition: box-shadow, border-color var(--transition, 0.2s);

        &:focus {
          border-color: var(--main-dark, #005197);
          box-shadow: 0 0 0 3px rgba(0, 81, 151, 0.3);
        }
      }
    }

    &-btn {
      padding-left: 15px;
      padding-right: 15px;
    }
  }

  &-dropdown {
    display: flex;
    align-items: center;
    margin-left: auto;

    &-title {
      color: var(--dark-light, #6d7179);
      font-size: 0.875rem;
      font-weight: 700;
      line-height: 22px;
      margin-right: 5px;
      margin-left: 30px;
    }

    &-select {
      height: 40px;

      .dropdown__header {
        height: 40px;
        background: var(--gray, #f9f9f9);
        border: none;
        min-width: 50px;
        padding: 0 10px;
        transition: var(--transition);

        @media (max-width: 740px) {
          height: 40px;
          width: auto;
          margin: 0;
        }

        &:hover {
          background: var(--gray-2, #eaeaea);
        }

        .svg-icon {
          margin-left: 3px;
          color: var(--dark-light, #6d7179);
          width: 12px;
          height: 12px;
        }
      }

      .dropdown__header-text {
        color: var(--dark, #262626);
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 500;
        line-height: 22px;
      }
    }
  }

  &-currency {
    .dropdown__content {
      right: 0;
      left: auto;
      width: 50px;

      &-item {
        text-align: center;
      }
    }
  }

  &-stand {
    .dropdown__content {
      right: 0;
      left: auto;
      width: 80px;

      &-item {
        text-align: center;
      }
    }
  }

  &-categories {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 2;
    height: 500px;
    opacity: 0;
    visibility: hidden;
    background: var(--white, #fff);
    border-top: 1px solid var(--gray-darker, #d5dae5);
    transition: opacity var(--transition, 0.2s);

    &--visible {
      opacity: 1;
      visibility: visible;
    }

    &-content {
      height: 100%;
    }
  }
}
</style>
